import {
  FeatureAppTrackingManager,
  FilterTracking,
  getDefaultTrackingEvent,
  LinkTracking,
} from '../hooks/use-tracking-manager';
import _ from 'lodash';
import {CategoryModels, ModelConfig} from '../typing/model';
import {GlobalModelData} from '../store/local-storage';
import {Dealer, UsedVehicle} from '../typing/used-inventory';
import {DEFAULT_FUEL_TYPE} from '../utils/app-constants';

export interface Content {
  CountdownRemainingTime?: number;
  CountdownTotalTime?: number;
  ItemCount?: number;
  ItemPosition?: number;
  Name?: string;
  RSSFeedsCount?: number;
  Type?: string;
}

export interface Link {
  name?: string;
  url?: string;
}

export interface File {
  name?: string;
  category?: string;
  type?: string;
  productName?: string;
  url?: string;
  language?: string;
}

export interface AccordionClick {
  accordingOpen?: boolean;
  linkUrl?: string;
  filterName?: string;
}

const fieldsErrorCodes = {
  'FE01': 'Something went wrong',
  'INV01': 'Invalid Zip',
  'INV02': 'Communication Error',
  '': 'FE00',
};

export const filterNameMap = {
  dealers: 'DID',
  year: 'MY',
  model: 'CG',
  zip: 'ZIP',
  distance: 'DI',
  price: 'PR',
  mileage: 'KR',
  transmissionType: 'GT',
  exteriorColor: 'EC',
  interiorColor: 'IC',
  fuelType: 'FT',
};

/**
 * Simple example of creating a tagging event for a button click
 * @param contentData
 * @param linkData
 * @param trackingManager
 */
export const onVWButtonClickEvent = (
  contentData: Content,
  linkData: Link,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  // These objects properties required to be filled based on the data
  // that we are able to manipulate
  customEvent.environment = {
    ...customEvent.environment,
    commerce: true,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Button click',
  };

  customEvent.content = {
    ...customEvent.content,
    ...contentData,
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
    customerType: undefined,
    customerContractsCount: undefined,
    dealerStatus: false,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent('VW_Button_Click', customEvent);
};

export const onVWBasicFilterSearchResultPageload = (
  filter: FilterTracking,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.filter = {
    ...customEvent.filter,
    ...filter,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_FilterSearchResult_Pageload',
      customEvent,
    );
};

export const onVWBasicFilterSelectClick = (
  filter: FilterTracking,
  link: LinkTracking,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.filter = {
    ...customEvent.filter,
    ...filter,
  };

  customEvent.link = {
    ...customEvent.link,
    ...link,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_FilterSelect_Click',
      customEvent,
    );
};

export const onVWBasicVehicleButtonClick = (
  vehicle: UsedVehicle,
  link: LinkTracking,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.configuration = {
    ...customEvent.configuration,
    Manufacturer: 'Volkswagen',
    CarlineName: vehicle?.model,
    CarlineId: vehicle?.modelCodeKey || undefined,
    SalesgroupId: vehicle?.modelCodeKey,
    SalesgroupName: vehicle?.trimLevel,
    EquipmentlineName: vehicle?.trimLevel,
    ModelId: vehicle?.modelCode,
    ModelName: vehicle?.model,
    ModelYear: vehicle?.modelYear,
    ModelDriveType: vehicle?.engineDescription,
    ModelGearType: vehicle?.transmissionDescription,
    PriceTotalTotal: vehicle?.adjustedListPrice,
    vehicleidentificationnumber: vehicle?.vin,
    EngineFuelTypePrimary:
      DEFAULT_FUEL_TYPE?.typeMap[
        vehicle?.engineFuelType ? vehicle?.engineFuelType : ''
      ],
    ExteriorColorCode: vehicle?.factoryExteriorCode,
    ExteriorColorName: vehicle?.exteriorColorDescription,
    InteriorColorCode: vehicle?.factoryInteriorCode,
    InteriorColorName: vehicle?.interiorColorDescription,
    PriceCurrency: 'USD',
    UsedVehicle: vehicle?.usedType === 'Y',
  };

  customEvent.link = {
    ...customEvent.link,
    ...link,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_VehicleButton_Click',
      customEvent,
    );
};

export const onVWBasicErrorMessageLoad = (
  trackingManager: FeatureAppTrackingManager,
  modelsConfigVar: CategoryModels[],
  globalModelData: GlobalModelData | undefined,
  errorId: string,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.environment = {
    ...customEvent.environment,
    commerce: false,
  };

  customEvent.user = {
    loginStatus: false,
  };

  if (globalModelData && globalModelData.slug) {
    let curModelConfig: ModelConfig | undefined;

    _.find(modelsConfigVar, function (categories) {
      _.find(categories.models, function (modelconfig) {
        if (modelconfig.slug === globalModelData?.slug) {
          curModelConfig = modelconfig;
          return true;
        }
        return false;
      });
    });

    customEvent.configuration = {
      Manufacturer: 'Volkswagen',
      BodyTypeName: curModelConfig?.category, //TODO Get category like sedan, compact that comes from the modelsdata. Either make query or use a apollo variable
      CarlineId: curModelConfig?.carlineId,
      CarlineName: curModelConfig?.name,
      PriceTotalCurrency: 'USD',
      UsedVehicle: false,
    };
  }

  customEvent.form = {
    orderId: '1',
  };

  customEvent.process = {
    Name: 'Validate Zip',
    StepName: 'Validate Zip',
    StepNumber: '1',
    Components: ['Zip Entry'],
  };

  customEvent.error = {
    code: errorId,
    message: fieldsErrorCodes[errorId],
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_ErrorMessage_Load',
      customEvent,
    );
};

/**
 * On Form Layer load event
 * @param name
 * @param trackingManager
 * @param store
 */
export const onVWBasicLinkClickEvent = (
  trackingManager: FeatureAppTrackingManager,
  contentId: string,
  link: {url: string; name: string; newTab?: boolean},
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.eventInfo = {...customEvent.eventInfo, contentId: contentId};
  customEvent.link = link;
  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Link_Click',
      customEvent,
    );
};

/**
 * handle zipcode change tracking
 * @param name
 * @param trackingManager
 */
export const onVWBasicLayerloadEvent = (
  trackingManager?: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.eventInfo = {...customEvent.eventInfo, contentId: 'ZIP Layer'};

  customEvent.user = {
    loginStatus: false,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Layer_Load',
      customEvent,
    );
};

/**
 * Handle VW Basic Vehicle Pageload
 * @param vehicleData
 * @param trackingManager
 */
export const onVWBasicVehiclePageload = (
  vehicleData: UsedVehicle,
  trackingManager: FeatureAppTrackingManager,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Vehicle page load',
    contentLabels: undefined,
  };

  customEvent.environment = {
    ...customEvent.environment,
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
  };

  customEvent.configuration = {
    ...customEvent.configuration,
    Manufacturer: 'Volkswagen',
    BodyTypeName: undefined,
    CarlineName: vehicleData?.model,
    CarlineId: vehicleData?.modelCodeKey || undefined,
    SalesgroupId: vehicleData?.modelCodeKey,
    SalesgroupName: vehicleData?.trimLevel,
    EquipmentlineName: vehicleData?.trimLevel,
    ModelId: vehicleData?.modelCode,
    ModelName: vehicleData?.model,
    ModelYear: vehicleData?.modelYear,
    PriceType: undefined,
    PriceTypeParameter: undefined,
    PriceRateCurrency: 'USD',
    PriceRateType: undefined,
    PriceRateBasic: vehicleData?.adjustedListPrice,
    PriceRateAdditional: undefined,
    PriceRateTotal: undefined,
    PriceTotalCurrency: 'USD',
    PriceTotalBasic: undefined,
    PriceTotalAdditional: undefined,
    PriceTotalTotal: undefined,
    UsedVehicle: vehicleData?.usedType === 'Y',
    UsedVehicleCondition: undefined,
    UsedVehicleRegistrationDate: undefined,
    UsedVehicleMileage: vehicleData?.odometer,
    UsedVehicleComissionNumber: undefined,
  };

  customEvent.partner = {
    informationBnr: vehicleData?.dealer?.dealerid,
    informationName: vehicleData?.dealer?.name,
    informationZIP: vehicleData?.dealer?.postalcode,
    informationDepartment: undefined,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Vehicle_Pageload',
      customEvent,
    );
};

/**
 * Handle VW Basic Download Click
 * @param linkData
 * @param fileData
 * @param trackingManager
 * @param vehicleData
 */
export const onVWBasicDownloadClick = (
  linkData: Link,
  fileData: File,
  trackingManager: FeatureAppTrackingManager,
  vehicleData?: UsedVehicle,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Download button click',
  };

  customEvent.environment = {
    ...customEvent.environment,
    commerce: true,
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
    customerType: undefined,
  };

  customEvent.configuration = {
    ...customEvent.configuration,
    Manufacturer: 'Volkswagen',
    BodyTypeName: undefined,
    CarlineId: vehicleData?.modelCodeKey,
    CarlineName: undefined,
    CarlineIsRecommendation: undefined,
    SalesgroupId: vehicleData?.modelCodeKey,
    SalesgroupName: vehicleData?.trimLevel,
    SalesgroupIsRecommendation: undefined,
    EquipmentlineName: vehicleData?.trimLevel,
    EquipmentlineIsRecommendation: undefined,
    ModelId: vehicleData?.modelCode,
    ModelTrimFeatures: undefined,
    ModelName: vehicleData?.model,
    ModelYear: vehicleData?.modelYear,
    ModelModelVersion: undefined,
    ModelIsRecommendation: undefined,
    ModelDriveType: vehicleData?.engineDescription,
    ModelGearType: vehicleData?.transmissionDescription,
    EnginePowerHPKW: undefined,
    EngineFuelTypePrimary:
      DEFAULT_FUEL_TYPE?.typeMap[
        vehicleData?.engineFuelType ? vehicleData?.engineFuelType : ''
      ],
    EngineFuelTypeNonPrimary: undefined,
    EmissionCo2CombinedPrimary: undefined,
    ConsumptionCombinedPrimary: undefined,
    ConsumptionCombinedNonPrimary: undefined,
    EmissionConsumptionEfficiencyClass: undefined,
    EmissionConsumptionTestingMethod: undefined,
    ExteriorColorCode: vehicleData?.factoryExteriorCode,
    ExteriorColorName: vehicleData?.exteriorColorDescription,
    ExteriorColorIsRecommendation: undefined,
    InteriorColorCode: vehicleData?.factoryInteriorCode,
    InteriorColorName: vehicleData?.interiorColorDescription,
    InteriorColorIsRecommendation: undefined,
    WheelsBasicId: undefined,
    WheelsBasicName: undefined,
    WheelsBasicIsRecommendation: undefined,
    PriceCurrency: 'USD',
    PriceBasis: undefined,
    PriceExterior: undefined,
    PriceOptions: undefined,
    PriceTotalConfiguration: undefined,
    PriceRecommendedOptions: undefined,
    PriceCustomerType: undefined,
    OptionsNumber: undefined,
    OptionsList: undefined,
    Marketingcode: undefined,
    UsedVehicle: vehicleData?.usedType === 'Y',
    UsedVehicleCondition: undefined,
    FocusItem: undefined,
    RemoteModelData: undefined,
  };

  customEvent.file = {
    ...fileData,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_Download_Click',
      customEvent,
    );
};

/**
 * Handle VW Basic Accordiong Open or Close Click
 * @param clickInfo
 * @param trackingManager
 * @param vehicleData
 */
export const onVWBasicAccordionClick = (
  clickInfo: AccordionClick,
  trackingManager: FeatureAppTrackingManager,
  vehicleData?: UsedVehicle,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.environment = {
    ...customEvent.environment,
    commerce: true,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: `Accordion ${clickInfo.accordingOpen ? 'open' : 'close'} click`,
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
  };

  customEvent.configuration = {
    ...customEvent.configuration,
    CarlineId: vehicleData?.modelCodeKey,
    CarlineName: undefined,
    SalesgroupId: vehicleData?.modelCodeKey,
    SalesgroupName: vehicleData?.trimLevel,
    EquipmentlineName: vehicleData?.trimLevel,
    RemoteModelData: undefined,
  };

  customEvent.link = {
    name: clickInfo?.filterName,
    url: clickInfo?.linkUrl,
  };

  customEvent.calculation = {
    ProductName: undefined,
    ProductType: undefined,
  };

  customEvent.filter = {
    name: clickInfo.filterName,
  };

  customEvent.accordion = {
    groupItemName: clickInfo.filterName,
    groupItemState: clickInfo.accordingOpen ? 'Open' : 'Close',
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      `VWBasic_Accordion${clickInfo.accordingOpen ? 'Open' : 'Close'}_Click`,
      customEvent,
    );
};

/**
 * Handle VW Basic Dealer Offer CTA Click
 * @param linkData
 * @param trackingManager
 * @param vehicleData
 */
export const onVWBasicDealerOfferCTAClick = (
  linkData: Link,
  trackingManager: FeatureAppTrackingManager,
  vehicleData?: UsedVehicle,
) => {
  let customEvent = getDefaultTrackingEvent();
  customEvent.environment = {
    ...customEvent.environment,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Dealer offer cta click',
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
  };

  customEvent.configuration = {
    ...customEvent.configuration,
    BodyTypeName: undefined,
    CarlineId: vehicleData?.modelCodeKey,
    CarlineName: undefined,
    SalesgroupId: vehicleData?.modelCodeKey,
    SalesgroupName: vehicleData?.trimLevel,
    EquipmentlineName: vehicleData?.trimLevel,
    ModelId: vehicleData?.modelCode,
    ModelName: vehicleData?.model,
    ModelYear: vehicleData?.modelYear,
    PriceType: undefined,
    PriceTypeParameter: undefined,
    PriceRateCurrency: 'USD',
    PriceRateType: undefined,
    PriceRateBasic: vehicleData?.adjustedListPrice,
    PriceRateAdditional: undefined,
    PriceRateTotal: undefined,
    PriceTotalCurrency: 'USD',
    PriceTotalBasic: undefined,
    PriceTotalAdditional: undefined,
    PriceTotalTotal: undefined,
    UsedVehicle: vehicleData?.usedType === 'Y',
    UsedVehicleCondition: undefined,
  };

  customEvent.partner = {
    informationBnr: vehicleData?.dealer?.dealerid,
    informationDepartment: undefined,
    informationName: vehicleData?.dealer?.name,
    informationZIP: vehicleData?.dealer?.postalcode,
  };

  customEvent.form = {
    FormType: 'make-offer',
    FormName: 'Make an Offer',
    FormVersion: '1',
    MarketingConsent: undefined,
    OpportunityUID: undefined,
    LeadID: undefined,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_DealerOfferCTA_Click',
      customEvent,
    );
};

/**
 * Handle VW Basic Dealer Button Click
 * @param linkData
 * @param trackingManager
 * @param vehicleData
 */
export const onVWBasicDealerButtonClick = (
  linkData: Link,
  trackingManager: FeatureAppTrackingManager,
  vehicleData?: UsedVehicle,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.environment = {
    ...customEvent.environment,
    commerce: true,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Dealer button click',
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
  };

  customEvent.content = {
    Type: 'contact-dealer',
    Name: 'Contact dealer',
    ItemCount: 1,
    ItemPosition: 1,
    Asset: undefined,
  };

  customEvent.configuration = {
    ...customEvent.configuration,
    BodyTypeName: undefined,
    CarlineId: vehicleData?.modelCodeKey,
    CarlineName: undefined,
    SalesgroupId: vehicleData?.modelCodeKey,
    SalesgroupName: vehicleData?.trimLevel,
    EquipmentlineName: vehicleData?.trimLevel,
    ModelId: vehicleData?.modelCode,
    ModelName: vehicleData?.model,
    ModelYear: vehicleData?.modelYear,
    PriceType: undefined,
    PriceTypeParameter: undefined,
    PriceRateCurrency: 'USD',
    PriceRateType: undefined,
    PriceRateBasic: vehicleData?.adjustedListPrice,
    PriceRateAdditional: undefined,
    PriceRateTotal: undefined,
    PriceTotalCurrency: 'USD',
    PriceTotalBasic: undefined,
    PriceTotalAdditional: undefined,
    PriceTotalTotal: undefined,
    UsedVehicle: vehicleData?.usedType === 'Y',
    UsedVehicleCondition: undefined,
  };

  customEvent.partner = {
    informationBnr: vehicleData?.dealer?.dealerid,
    informationDepartment: undefined,
    informationName: vehicleData?.dealer?.name,
    informationZIP: vehicleData?.dealer?.postalcode,
  };

  customEvent.form = {
    FormType: 'contact-dealer',
    FormName: 'Contact Dealer',
    FormVersion: '1',
    MarketingConsent: undefined,
    OpportunityUID: undefined,
    LeadID: undefined,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_DealerButton_Click',
      customEvent,
    );
};

/**
 * Handle VW Basic Dealer Link Click
 * @param linkData
 * @param trackingManager
 * @param dealer
 */
export const onVWBasicDealerLinkClick = (
  linkData: Link,
  trackingManager: FeatureAppTrackingManager,
  dealer?: Dealer,
) => {
  let customEvent = getDefaultTrackingEvent();

  customEvent.environment = {
    ...customEvent.environment,
  };

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Dealer link click',
  };

  customEvent.user = {
    ...customEvent.user,
    loginStatus: false,
    customerType: undefined,
  };

  customEvent.partner = {
    informationBnr: dealer?.dealerid,
    informationDepartment: undefined,
    informationName: dealer?.name,
    informationZIP: dealer?.postalcode,
  };

  customEvent.link = {
    ...linkData,
  };

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_DealerLink_Click',
      customEvent,
    );
};

/**
 * Handle vehicle telephone number click event
 * @param trackingManager
 * @param modelsConfigVar
 * @param vehicle
 * @param linkParams
 */
export const onVWBasicVehicleTelephoneNumberClick = (
  trackingManager: FeatureAppTrackingManager,
  vehicle?: UsedVehicle,
  linkParams?: Link,
) => {
  let curModelConfig: ModelConfig | undefined;
  let customEvent = getDefaultTrackingEvent();

  customEvent.content = {
    Asset: undefined,
    ItemCount: undefined,
    ItemPosition: undefined,
    Name: undefined,
    Type: undefined,
  };

  customEvent.process = {
    StepName: undefined,
  };

  customEvent.link = {
    ...linkParams,
  };

  if (curModelConfig) {
    let curModelTrim = _.find(curModelConfig.modelTrims, function(modeltrim) {
      return modeltrim.trimId === vehicle?.trimLevel;
    });

    customEvent.environment = {
      ...customEvent.environment,
      region: vehicle?.dealer?.state || "",
    };

    if (vehicle?.dealer)
      customEvent.partner = {
        informationBnr: vehicle?.dealer.dealerid,
        informationName: vehicle?.dealer.name,
        informationZIP: vehicle?.dealer.postalcode,
        informationDistance: vehicle?.dealer.distance,
        informationDepartment: undefined,
      };

    customEvent.configuration = {
      BodyTypeName: curModelConfig.category,
      CarlineId: curModelConfig.carlineId,
      CarlineName: curModelConfig.name,
      SalesgroupId:
        curModelTrim && curModelTrim.salesgroupId
          ? curModelTrim.salesgroupId
          : undefined,
      SalesgroupName:
        curModelTrim && curModelTrim.name ? curModelTrim.name : undefined,
      EquipmentlineName: vehicle?.trimLevel,
      ModelId: vehicle?.modelCode,
      ModelName: vehicle?.model,
      ModelYear: vehicle?.modelYear,
      // ExteriorColorCode: vehicle?.exteriorColorCode,
      ExteriorColorName: vehicle?.exteriorColorDescription,
      ExteriorColorIsRecommendation: false,
      // InteriorColorCode: vehicle?.interiorColorCode,
      InteriorColorName: vehicle?.interiorColorDescription,
      InteriorColorIsRecommendation: false,
      PriceTotalTotal: vehicle?.msrp,
      PriceTotalCurrency: 'USD',
      vehicleidentificationnumber: vehicle?.vin,
      ModelGearType: undefined,
      PriceType: undefined,
      PriceTypeParameter: undefined,
      PriceRateCurrency: 'USD',
      PriceRateType: undefined,
      PriceRateBasic: vehicle?.msrp,
      PriceRateAdditional: undefined,
      PriceRateTotal: undefined,
      PriceTotalBasic: undefined,
      PriceTotalAdditional: undefined,
    };
  }

  trackingManager &&
    trackingManager.trackFeatureAppGlobalEvent(
      'VWBasic_VehicleTelephoneNumber_Click',
      customEvent,
    );
};

interface IAdditionalTrackingData {
  link?: ILink;
  partner?: IPartner;
}

export interface ILink {
  url?: string;
  name?: string;
  newTab?: boolean;
  isDealerPage?: boolean;
}
export interface IPartner {
  informationBnr?: string;
  informationName?: string;
  informationDepartment?: string;
  informationZIP?: string;
  informationDistance?: number;
  SearchNumberOfResults?: number;
  onlinePaymentOption?: boolean;
  appointmentSystemName?: string;
  appointmentSystemUtilization?: boolean;
  informationVendor?: string;
}

/**
 * handle vehicle button click
 * @param vehicle
 * @param modelsConfigVar
 * @param contentData
 * @param trackingManager
 * @param additionalTrackingData
 */
export const onVWBasicVehicleButtonClickEvent = (
  vehicle: UsedVehicle,
  contentData: Content,
  trackingManager: FeatureAppTrackingManager,
  additionalTrackingData?: IAdditionalTrackingData,
) => {
  let customEvent = getDefaultTrackingEvent();
  let curModelConfig: ModelConfig | undefined;

  customEvent.eventInfo = {
    ...customEvent.eventInfo,
    contentId: 'Vehicle button click',
  };

  if (additionalTrackingData?.link) {
    customEvent.link = additionalTrackingData.link;
  }

  if (curModelConfig) {
    customEvent.configuration = getVehicleConfiguration(
      vehicle,
      curModelConfig,
    );

    if (vehicle?.dealer)
      customEvent.partner = {
        informationBnr: vehicle.dealer.dealerid,
        informationName: vehicle.dealer.name,
        informationZIP: vehicle.dealer.postalcode,
        informationDistance: vehicle.dealer.distance,
      };

    if (additionalTrackingData?.partner) {
      customEvent.partner = {
        ...(customEvent?.partner && customEvent.partner),
        ...additionalTrackingData.partner,
      };
    }

    customEvent.content = contentData;
    trackingManager &&
      trackingManager.trackFormsGlobalEvent(
        'VWBasic_VehicleButton_Click',
        customEvent,
      );
  }
};

/**
 * gets vehicle configuration data
 * @param vehicle
 * @param curModelConfig
 */
const getVehicleConfiguration = (
  vehicle: UsedVehicle,
  curModelConfig: ModelConfig,
) => {
  let curModelTrim = _.find(curModelConfig.modelTrims, function(modeltrim) {
    return modeltrim.trimId === vehicle.trimLevel;
  });

  return {
    Manufacturer: 'Volkswagen',
    BodyTypeName: curModelConfig.category,
    CarlineId: curModelConfig.carlineId,
    CarlineName: curModelConfig.name,
    SalesgroupId:
      curModelTrim && curModelTrim.salesgroupId
        ? curModelTrim.salesgroupId
        : undefined,
    SalesgroupName:
      curModelTrim && curModelTrim.name ? curModelTrim.name : undefined,
    EquipmentlineName: vehicle.trimLevel,
    ModelId: vehicle.modelCode,
    ModelName: vehicle.model,
    ModelYear: vehicle.modelYear,
    ExteriorColorName: vehicle.exteriorColorDescription,
    ExteriorColorIsRecommendation: false,
    InteriorColorName: vehicle.interiorColorDescription,
    InteriorColorIsRecommendation: false,
    PriceTotalTotal: vehicle.msrp,
    PriceTotalCurrency: 'USD',
    UsedVehicle: false,
    vehicleidentificationnumber: vehicle.vin,
    matchtype: 'exact',
  };
};
